<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed, ref } from 'vue';
import { formatPickup, formatMoney } from '../format';
import type { OrderChange } from '../orderEdit';
import type { PdsCancellation, PdsOrder } from '../types';
import AmountRow from './AmountRow.vue';
import ClientMessageField from './ClientMessageField.vue';
import InfoNotice from './InfoNotice.vue';
import ReasonPicker from './ReasonPicker.vue';

const props = defineProps<{
  order: PdsOrder;
  changes: OrderChange[];
  refundCents: number;
  busy?: boolean;
}>();
const emit = defineEmits<{
  close: [];
  confirm: [modification: PdsCancellation];
}>();

// Le motif est obligatoire (traçabilité, remboursement) ; le message au client est facultatif, comme à l'annulation.
const reasons = ref<string[]>([]);
const message = ref('');
const valid = computed(() => !!reasons.value.length);

const confirm = () => {
  if (props.busy || !valid.value) return;
  emit('confirm', { reasons: reasons.value, message: message.value.trim() });
};
</script>

<template>
  <PdsModal size="lg" :dismissible="!busy" @close="emit('close')">
    <template #title>Valider les modifications</template>
    <template #subtitle>
      #{{ order.number }} · {{ order.label }} · retrait
      {{ formatPickup(order.dueAt) }}
    </template>

    <div class="flex flex-col gap-[18px] px-5 py-6">
      <p
        class="text-encre-douce text-[12px] leading-[14px] font-semibold tracking-[0.9px] uppercase"
      >
        Ce qui change
      </p>
      <ul class="flex flex-col">
        <li
          v-for="change in changes"
          :key="change.item.id"
          class="border-filet-doux flex h-11 items-center gap-[10px] border-t text-[14px] leading-[20px] last:border-b"
        >
          <span class="min-w-0 flex-1 truncate font-semibold">
            {{ change.item.name }}
          </span>
          <span class="text-encre-douce text-[12px]">
            {{ change.to ? `${change.from} → ${change.to}` : 'supprimé' }}
          </span>
          <span class="w-[72px] text-right">
            −{{ formatMoney(change.refundCents) }}
          </span>
        </li>
      </ul>
      <AmountRow
        label="À rembourser"
        :amount="formatMoney(refundCents)"
        :boxed="false"
      />
      <div class="flex flex-col gap-3">
        <p
          class="text-encre-douce text-[12px] font-semibold tracking-[0.9px] uppercase"
        >
          Motif · au moins un
        </p>
        <ReasonPicker v-model="reasons" :disabled="busy" />
      </div>
      <div class="bg-creux flex flex-col gap-1 rounded-[24px] px-3 pt-3 pb-2">
        <div class="flex items-baseline justify-between gap-3 px-1">
          <p
            class="text-encre-douce text-[12px] font-semibold tracking-[0.9px] uppercase"
          >
            Message au client · facultatif
          </p>
          <p class="text-encre-douce text-[12px]">
            visible sur sa page de commande
          </p>
        </div>
        <ClientMessageField
          v-model="message"
          placeholder="Dites-lui ce qui a changé et pourquoi"
        />
      </div>
      <InfoNotice title="Le client sera prévenu">
        Le remboursement est automatique. Le SMS renvoie le client vers sa
        commande, sans en détailler le contenu.
      </InfoNotice>
      <p class="text-encre-douce text-[12px] leading-[17px]">
        Montant estimé d'après le prix de chaque ligne : le serveur calcule et
        rembourse le montant exact.
      </p>
    </div>

    <template #footer>
      <PdsButton variant="second" fit :disabled="busy" @click="emit('close')">
        Retour
      </PdsButton>
      <span
        v-if="!valid"
        role="alert"
        class="text-alerte-texte text-[12px] max-sm:hidden sm:ml-auto"
      >
        Cochez au moins un motif
      </span>
      <PdsButton
        fit
        :loading="busy"
        :disabled="!valid"
        :class="valid && 'sm:ml-auto'"
        @click="confirm"
      >
        Rembourser {{ formatMoney(refundCents) }}
      </PdsButton>
    </template>
  </PdsModal>
</template>
